import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FeaturedEventCard } from '../components/FeaturedEventCard';
import { db, collection, getDocs } from '../config/firebase';
import { query, orderBy, limit, where } from 'firebase/firestore';
import { Event } from '../types/models';
import { Timestamp } from 'firebase/firestore';
import { MdCloudOff } from 'react-icons/md';
import { Calendar, Users, MapPin, TrendingUp } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import { useAuth } from '../contexts/AuthContext';
import { FiHeart } from 'react-icons/fi';
import { differenceInDays } from 'date-fns';
import { animateScroll as scroll, scroller } from 'react-scroll';
import { EventCard } from '../components/EventCard';
import { format } from 'date-fns';
import gsap from 'gsap';

// Local storage key for events (same as in CreateEvent.tsx)
const LOCAL_EVENTS_KEY = 'eventory_local_events';

// Helper function to validate event data (loosened)
const isValidEvent = (event: any): boolean => {
  return Boolean(event && event.id && event.title && typeof event.title === 'string');
};

// Robust event fetching with fallback logic
const getRobustEvents = async (): Promise<Event[]> => {
  const eventsRef = collection(db, 'events');
  // 1. Try published events with startDateTime
  let eventsQuery = query(
    eventsRef,
    where('status', '==', 'published'),
    orderBy('startDateTime', 'asc'),
    limit(24)
  );
  let snapshot = await getDocs(eventsQuery);
  let events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Event[];
  if (events.length > 0) return events;

  // 2. Fallback: all published events (ignore startDateTime)
  eventsQuery = query(
    eventsRef,
    where('status', '==', 'published'),
    limit(24)
  );
  snapshot = await getDocs(eventsQuery);
  events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Event[];
  if (events.length > 0) return events;

  // 3. Fallback: all events (any status)
  eventsQuery = query(eventsRef, limit(24));
  snapshot = await getDocs(eventsQuery);
  events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Event[];
  return events;
};

// Categories for filtering
const categories = ['All', 'Music', 'Technology', 'Business', 'Arts', 'Sports', 'Food', 'Education'];

const heroSlides = [
  {
    image: '/images/hero-banner.jpg',
  },
  {
    image: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1500&q=80',
    headline: 'Business & Networking',
    sub: 'Grow your network and skills at top business events.',
    tag: 'Business',
    cta: 'Explore Business Events',
  },
  {
    image: 'https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=1500&q=80',
    headline: 'Arts & Culture',
    sub: 'Discover art shows, exhibitions, and more.',
    tag: 'Arts',
    cta: 'See Arts Events',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 120, damping: 10 } },
};

const Home: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [featuredEvents, setFeaturedEvents] = useState<Event[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const gsapBgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch featured events
        const featuredQuery = query(
          collection(db, 'events'),
          where('status', '==', 'published'),
          orderBy('startDateTime', 'asc'),
          limit(8)
        );
        const featuredSnapshot = await getDocs(featuredQuery);
        const featured = featuredSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Event));
        setFeaturedEvents(featured);

        // Fetch upcoming events
        const upcomingQuery = query(
          collection(db, 'events'),
          where('status', '==', 'published'),
          orderBy('startDateTime', 'asc'),
          limit(8)
        );
        const upcomingSnapshot = await getDocs(upcomingQuery);
        const upcoming = upcomingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Event));
        setUpcomingEvents(upcoming);
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Failed to load events. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    if (!gsapBgRef.current) return;
    const text = gsapBgRef.current.querySelector('#eventory-animated-text');
    if (!text) return;
    const letters = text.querySelectorAll('span');
    if (!letters.length) return;

    // Animate each letter in a wave
    const tl = gsap.timeline({ repeat: -1, yoyo: true });
    tl.to(letters, {
      y: (i) => Math.sin(i) * 10 + 10,
      scale: 1.2,
      rotation: (i) => (i - 3.5) * 8,
      color: '#fff',
      filter: 'drop-shadow(0 2px 16px #a78bfa)',
      duration: 1.2,
      stagger: {
        each: 0.12,
        yoyo: true,
        repeat: 1,
        from: 'center',
      },
      ease: 'elastic.inOut(1, 0.5)',
    })
    .to(letters, {
      color: '#f472b6',
      filter: 'drop-shadow(0 2px 24px #f472b6)',
      duration: 0.7,
      stagger: {
        each: 0.09,
        from: 'edges',
      },
      ease: 'power1.inOut',
    }, '-=0.8')
    .to(letters, {
      y: 0,
      scale: 1,
      rotation: 0,
      color: '#a5b4fc',
      filter: 'drop-shadow(0 2px 12px #a5b4fc)',
      duration: 1.1,
      stagger: {
        each: 0.09,
        from: 'center',
      },
      ease: 'expo.inOut',
    }, '+=0.2');

    return () => { tl.kill(); };
  }, []);

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'Date TBA';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM d, yyyy');
    } catch (e) {
      console.error("Error formatting date:", timestamp, e);
      return 'Date TBA';
    }
  };

  const formatTime = (timestamp: any): string => {
    if (!timestamp) return 'Time TBA';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'h:mm a');
    } catch (e) {
      console.error("Error formatting time:", timestamp, e);
      return 'Time TBA';
    }
  };

  const getBestEventImage = (event: Event): string => {
    if (event.coverImage && typeof event.coverImage === 'string' && event.coverImage.trim()) return event.coverImage;
    if (event.imageUrl && typeof event.imageUrl === 'string' && event.imageUrl.trim()) return event.imageUrl;
    if (event.media?.images?.[0] && typeof event.media.images[0] === 'string' && event.media.images[0].trim()) return event.media.images[0];
    return '/placeholder-event.jpg';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Oops!</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-gray-50 via-white to-gray-100">
      {/* Full-width Hero Swiper */}
      <Swiper
        modules={[Navigation, Pagination, A11y]}
        spaceBetween={0}
        slidesPerView={1}
        navigation
        pagination={{ clickable: true }}
        className="w-full h-[420px] md:h-[520px] lg:h-[600px]"
      >
        {heroSlides.map((slide, idx) => (
          <SwiperSlide key={idx}>
            <div
              className="relative w-full h-full flex items-center justify-center bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              {slide.headline ? (
                <>
                  <div className="absolute inset-0 bg-black/40" />
                  <div className="relative z-10 flex flex-col items-center justify-center text-center w-full h-full px-4">
                    <h1 className="text-4xl md:text-6xl font-extrabold text-white drop-shadow mb-4">{slide.headline}</h1>
                    <p className="text-lg md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">{slide.sub}</p>
                    <button
                      className="px-8 py-4 bg-white text-indigo-900 font-bold text-lg rounded-full shadow-lg hover:bg-indigo-100 transition"
                      onClick={() => navigate(`/events/category/${encodeURIComponent(slide.tag.toLowerCase())}`)}
                    >
                      {slide.cta}
                    </button>
                  </div>
                </>
              ) : (
                <Link to="/events" className="absolute inset-0" aria-label="Browse all events" />
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {/* Category Filter (full width, with scroll targets) */}
      <section className="py-6 bg-white border-b w-full">
        <div className="flex flex-wrap gap-2 justify-center w-full" id="category-tags">
          {categories.map(category => (
            <button
              key={category}
              id={`tag-${category.toLowerCase()}`}
              className={`px-4 py-2 rounded-full font-semibold border transition-all duration-200 text-base shadow-sm
                ${selectedCategory === category
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                  : 'bg-white text-indigo-700 border-gray-300 hover:bg-indigo-50'}
              `}
              onClick={() => {
                setSelectedCategory(category);
                if (category === 'All') {
                  scroll.scrollToTop({ duration: 500 });
                } else {
                  scroller.scrollTo('featured-events-section', {
                    duration: 800,
                    delay: 0,
                    smooth: 'easeInOutQuart',
                    offset: -100,
                  });
                }
              }}
            >
              {category}
            </button>
          ))}
        </div>
      </section>
      {/* Featured Events Section (full width) */}
      <motion.section 
        className="py-20 px-4 bg-gray-50 w-full" 
        initial={{ opacity: 0, y: 40 }} 
        whileInView={{ opacity: 1, y: 0 }} 
        viewport={{ once: true }} 
        transition={{ duration: 0.7 }}
        id="featured-events-section"
      >
        <div className="w-full px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Events</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Discover handpicked events that promise unforgettable experiences
            </p>
          </div>
          {featuredEvents.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {featuredEvents.map((event, index) => {
                console.log('Featured Event data:', event);
                return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.04, boxShadow: '0 8px 32px rgba(80,80,180,0.12)' }}
                  transition={{ duration: 0.3 }}
                >
                  <FeaturedEventCard
                    id={event.id}
                    title={event.title}
                    description={event.description}
                    date={formatDate(event.startDateTime)}
                    time={formatTime(event.startDateTime)}
                    location={event.location?.venue || event.location?.address || event.location?.city || '—'}
                    category={event.category}
                    imageUrl={getBestEventImage(event)}
                    index={index}
                  />
                </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center text-center text-gray-500 py-16 text-xl">
              <div className="mb-6">
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="60" cy="100" rx="40" ry="10" fill="#EEF2FF" />
                  <rect x="30" y="30" width="60" height="40" rx="8" fill="#6366F1" fillOpacity="0.12" />
                  <rect x="40" y="40" width="40" height="20" rx="4" fill="#6366F1" fillOpacity="0.18" />
                  <rect x="50" y="50" width="20" height="4" rx="2" fill="#6366F1" fillOpacity="0.25" />
                  <circle cx="60" cy="60" r="6" fill="#6366F1" fillOpacity="0.25" />
                </svg>
              </div>
              <div className="mb-2 text-2xl font-semibold text-gray-700">No featured events yet</div>
              <div className="mb-4 text-base text-gray-500 max-w-md">
                We couldn't find any featured events at the moment. Please check back later or <a href="/events" className="text-indigo-600 hover:underline">explore all events</a>.
              </div>
            </div>
          )}
        </div>
      </motion.section>
      {/* Upcoming Events Section */}
      <section className="py-20 w-full bg-white">
        <div className="w-full px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Events For You</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Explore trending and recommended events tailored for you
            </p>
          </div>
          {upcomingEvents.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {upcomingEvents.map((event, index) => {
                console.log('Upcoming Event data:', event);
                return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.04, boxShadow: '0 8px 32px rgba(80,80,180,0.12)' }}
                  transition={{ duration: 0.3 }}
                >
                  <FeaturedEventCard
                    id={event.id}
                    title={event.title}
                    description={event.description}
                    date={formatDate(event.startDateTime)}
                    time={formatTime(event.startDateTime)}
                    location={event.location?.venue || event.location?.address || event.location?.city || '—'}
                    category={event.category}
                    imageUrl={getBestEventImage(event)}
                    index={index}
                  />
                </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12 text-lg">No events found for your selection.</div>
          )}
        </div>
      </section>
      {/* CTA Section - FULL PAGE HERO */}
      <section className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center text-center bg-gradient-to-br from-indigo-800 to-purple-900 overflow-hidden px-8 py-16 md:px-16 md:py-20">
        {/* Abstract shapes for background animation */  }
        <div ref={gsapBgRef} className="absolute inset-0 z-0 opacity-50">
          <div className="absolute w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-80 animate-blob top-1/4 left-1/4"></div>
          <div className="absolute w-96 h-96 bg-yellow-400 rounded-full mix-blend-multiply filter blur-3xl opacity-80 animate-blob animation-delay-2000 top-1/2 left-1/2"></div>
          <div className="absolute w-96 h-96 bg-fuchsia-400 rounded-full mix-blend-multiply filter blur-3xl opacity-80 animate-blob animation-delay-4000 bottom-1/4 right-1/4"></div>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          className="relative z-10 flex flex-col items-center justify-center w-full max-w-5xl mx-auto text-white"
        >
          <motion.h2
            className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight drop-shadow-2xl tracking-tight"
          >
            <motion.span variants={itemVariants} className="inline-block">Unlock</motion.span>{" "}
            <motion.span variants={itemVariants} className="inline-block">Unforgettable</motion.span>{" "}
            <motion.span variants={itemVariants} className="inline-block">Experiences.</motion.span>
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-lg md:text-xl lg:text-2xl mb-12 font-light max-w-3xl opacity-90 leading-relaxed"
          >
            From local gigs to global festivals, discover and book events that match your passion.
          </motion.p>
          {!user && (
            <motion.div variants={itemVariants}>
              <Link
                to="/register"
                className="relative inline-flex items-center px-12 py-5 bg-white text-indigo-800 rounded-full font-bold text-xl transition-all duration-500 overflow-hidden shadow-2xl group transform hover:-translate-y-1 hover:scale-105 animate-button-glow-pulse"
              >
                <span className="relative z-10">
                  Find Your Next Event
                </span>
                <svg
                  className="w-6 h-6 ml-3 transition-transform duration-300 group-hover:translate-x-1 group-hover:rotate-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
                <span className="absolute inset-0 bg-indigo-100 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
              </Link>
            </motion.div>
          )}
        </motion.div>
      </section>
    </div>
  );
};

export default Home; 